import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  Checkbox,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  SxProps,
  TextFieldProps,
  Typography,
} from '@mui/material';

import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';

import {
  adminCreateRole,
  adminUpdateRole,
  IRole,
  IRolePermissions,
  TAdminCreateRole,
  TAdminUpdateRole,
} from '../../../../../../store/V2/RolesSlice';
import { useAppSelector } from '../../../../../../hooks';
import { TP_COLORS, userRoleTypes } from '../../../../../../constants';
import { capitalizeFirstLetter } from '../../../../../../helper';
import { getSettingsState } from '../../../../../../store/V2/SettingsSlice';
import {
  TPAutoCompleteArrowIconComponent,
  TPTextField,
} from '../../../../../../components/Inputs';
import DarkLoadingButton, {
  defaultButtonStyles,
} from '../../../../../../components/V2/Buttons';

const dialogTitleStyles: SxProps = {
  m: 0,
  p: { xs: '15px', sm: '30px' },
  color: '#000000',
  fontWeight: 600,
  fontSize: '16px',
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const formControlLabelStyles: SxProps = {
  '& .MuiCheckbox-root': {
    padding: 1,
  },
  '& .MuiFormControlLabel-label': {
    fontSize: '14px',
    color: '#555',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    lineHeight: 1.2,
  },
};

interface AddRoleModalProps {
  open: boolean;
  onClose: () => void;
  role?: IRole;
  readOnly?: boolean;
  duplicate?: boolean;
}

type TSelectOption = {
  label: string;
  value: string;
};

type TState = {
  name: string;
  selectedType: TSelectOption;
  userPermissionsChecked: boolean;
  adminPermissionsChecked: boolean;
};

const initialState: TState = {
  name: '',
  selectedType: userRoleTypes[0],
  userPermissionsChecked: false,
  adminPermissionsChecked: false,
};

const AddRoleModal: FunctionComponent<AddRoleModalProps> = ({
  open,
  onClose,
  role,
  readOnly = false,
  duplicate = false,
}) => {
  const dispatch = useDispatch();

  // Store
  const { adminPermissionsList } = useAppSelector(getSettingsState);

  // State
  const [state, setState] = useState({ ...initialState });
  const [isLoading, setLoading] = useState(false);
  const [
    checkedPermissions,
    setCheckedPermissions,
  ] = useState<IRolePermissions>({});

  let mode: 'edit' | 'new' | 'view' | 'duplicate' = 'new';
  let modalTitle = 'Add New Role';

  const isSubmitDisabled = !state.name.trim().length || !state.selectedType;

  if (role) {
    mode = 'edit';
    modalTitle = 'Edit Role';

    if (readOnly) {
      mode = 'view';
      modalTitle = 'Role Details';
    } else if (duplicate) {
      mode = 'duplicate';
      modalTitle = 'Add New Role';
    }
  }

  useEffect(() => {
    if (mode !== 'new' && role) {
      setCheckedPermissions(role.permissions);

      setState((prevState) => {
        const newState = {
          ...prevState,
          name: role.name,
          userPermissionsChecked: true,
          adminPermissionsChecked: true,
        };

        if (role.type) {
          const selectedValue = userRoleTypes.find(
            (item) => item.value === role.type
          );
          newState.selectedType = selectedValue || userRoleTypes[0];
        }

        if (adminPermissionsList) {
          newState.userPermissionsChecked = Object.keys(role.permissions).some(
            (key) => key in adminPermissionsList.user
          );

          newState.adminPermissionsChecked = Object.keys(role.permissions).some(
            (key) => key in adminPermissionsList.admin
          );
        }

        return newState;
      });
    }
  }, []);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  // Function to handle checkbox changes
  const handleCheckboxChange = (section, action) => {
    setCheckedPermissions((prevPermissions) => {
      const sectionPermissions = prevPermissions[section] || [];
      const isChecked = sectionPermissions.includes(action);

      const updatedSectionPermissions = isChecked
        ? sectionPermissions.filter((item) => item !== action) // Remove action if checked
        : [...sectionPermissions, action]; // Add action if not checked

      // If no permissions are left in the section, remove the section from checkedPermissions
      const updatedPermissions = {
        ...prevPermissions,
        [section]:
          updatedSectionPermissions.length > 0
            ? updatedSectionPermissions
            : undefined,
      };

      // Remove empty sections from the object
      const finalPermissions = Object.fromEntries(
        Object.entries(updatedPermissions).filter(([_, actions]) => actions)
      );

      return Object.keys(finalPermissions).length > 0 ? finalPermissions : {};
    });
  };

  const handleSubmit = async () => {
    setLoading(true);

    if (mode === 'new' || mode === 'duplicate') {
      const apiData: TAdminCreateRole = {
        name: state.name,
        type: state.selectedType.value,
        permissions: checkedPermissions,
      };

      await dispatch(adminCreateRole(apiData));

      onClose();
    } else if (mode === 'edit' && role) {
      const apiData: TAdminUpdateRole = {
        id: role._id,
        name: state.name,
        type: state.selectedType.value,
        permissions: checkedPermissions,
      };

      await dispatch(adminUpdateRole(apiData));

      onClose();
    }

    setLoading(false);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: '20px' },
      }}
    >
      <DialogTitle sx={dialogTitleStyles}>{modalTitle}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: { xs: '7px', sm: '22px' },
          top: { xs: '7px', sm: '22px' },
        }}
      >
        <CloseIcon sx={{ color: '#000000' }} />
      </IconButton>
      <DialogContent
        sx={{ p: { xs: '0 15px 15px 15px', sm: '0 30px 30px 30px' } }}
      >
        <div style={{ paddingTop: '6px' }}>
          <Grid container spacing="20px">
            <Grid item xs={12}>
              <FormControl fullWidth>
                <TPTextField
                  {...textFieldProps}
                  label="Name *"
                  placeholder="Enter here..."
                  value={state.name}
                  InputProps={{ readOnly }}
                  onChange={(event) =>
                    updateStateAttributes({ name: event.target.value })
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Autocomplete
                  disableClearable
                  fullWidth
                  size="small"
                  disabled={readOnly}
                  value={state.selectedType}
                  options={userRoleTypes}
                  popupIcon={TPAutoCompleteArrowIconComponent}
                  onChange={(event, value) => {
                    updateStateAttributes({ selectedType: value });
                  }}
                  getOptionLabel={(option) =>
                    capitalizeFirstLetter(option.label)
                  }
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...textFieldProps}
                      size="small"
                      label="Type *"
                      placeholder="Type"
                    />
                  )}
                />
              </FormControl>
            </Grid>
            {adminPermissionsList && adminPermissionsList.user && (
              <Grid item xs={12}>
                <FormControlLabel
                  label="User Permissions"
                  control={
                    <Checkbox
                      disabled={readOnly}
                      checked={state.userPermissionsChecked}
                      onChange={(event, value) =>
                        updateStateAttributes({
                          userPermissionsChecked: value,
                        })
                      }
                    />
                  }
                />
                <Collapse in={state.userPermissionsChecked}>
                  <Grid container spacing={3}>
                    {Object.entries(adminPermissionsList.user).map(
                      ([section, actions]) => (
                        <PermissionSection
                          readOnly={readOnly}
                          key={section}
                          section={section}
                          actions={actions}
                          checkedPermissions={checkedPermissions}
                          onCheckboxChange={handleCheckboxChange}
                        />
                      )
                    )}
                  </Grid>
                </Collapse>
              </Grid>
            )}
            {adminPermissionsList && adminPermissionsList.admin && (
              <Grid item xs={12}>
                <FormControlLabel
                  label="Admin Permissions"
                  control={
                    <Checkbox
                      disabled={readOnly}
                      checked={state.adminPermissionsChecked}
                      onChange={(event, value) =>
                        updateStateAttributes({
                          adminPermissionsChecked: value,
                        })
                      }
                    />
                  }
                />
                <Collapse in={state.adminPermissionsChecked}>
                  <Grid container spacing={3}>
                    {Object.entries(adminPermissionsList.admin).map(
                      ([section, actions]) => (
                        <PermissionSection
                          readOnly={readOnly}
                          key={section}
                          section={section}
                          actions={actions}
                          checkedPermissions={checkedPermissions}
                          onCheckboxChange={handleCheckboxChange}
                        />
                      )
                    )}
                  </Grid>
                </Collapse>
              </Grid>
            )}
          </Grid>
        </div>
      </DialogContent>
      {mode !== 'view' ? (
        <DialogActions sx={{ p: { xs: '15px', sm: '30px' } }}>
          <DarkLoadingButton
            disableElevation
            variant="contained"
            color="inherit"
            loading={isLoading}
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            sx={{ ...defaultButtonStyles }}
          >
            {mode === 'edit' ? 'Save' : 'Add'}
          </DarkLoadingButton>
        </DialogActions>
      ) : null}
    </Dialog>
  );
};

const PermissionSection = ({
  readOnly = false,
  section,
  actions,
  checkedPermissions,
  onCheckboxChange,
}) => {
  const [expanded, setExpanded] = useState(false);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  return (
    <Grid item xs={12} md={6}>
      <Card
        variant="elevation"
        sx={{ borderRadius: 2, backgroundColor: TP_COLORS.paperGray.light }}
      >
        <CardContent sx={{ padding: '10px 16px !important' }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              fontSize="14px"
              color="primary"
              className="ellipsis-text one-line"
              title={section.charAt(0).toUpperCase() + section.slice(1)}
            >
              {section.charAt(0).toUpperCase() + section.slice(1)}
            </Typography>
            <IconButton onClick={handleExpandClick} size="small">
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Divider sx={{ mb: 1 }} />
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Grid container spacing={1}>
              {actions.map((action) => (
                <Grid
                  item
                  xs={12}
                  sm={actions.length > 5 ? 12 : 6}
                  key={action}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        disabled={readOnly}
                        color="primary"
                        checked={
                          checkedPermissions[section]?.includes(action) || false
                        }
                        onChange={() => onCheckboxChange(section, action)}
                      />
                    }
                    label={action.charAt(0).toUpperCase() + action.slice(1)}
                    sx={formControlLabelStyles}
                  />
                </Grid>
              ))}
            </Grid>
          </Collapse>
        </CardContent>
      </Card>
    </Grid>
  );
};

export default AddRoleModal;
